import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPrice, formatApiErrorDetail, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { Gavel, Trophy, History, Zap, Shield, Radio } from "lucide-react";
import { toast } from "sonner";
import AuctionTimer from "./AuctionTimer";
import useAuctionSocket from "../lib/useAuctionSocket";

export default function AuctionPanel({ listing, onUpdate }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [bids, setBids] = useState([]);
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [buyingNow, setBuyingNow] = useState(false);
    const [buyNowOpen, setBuyNowOpen] = useState(false);
    const [error, setError] = useState("");
    const [pulse, setPulse] = useState(false);

    const starting = Number(listing.starting_price || 0);
    const current = Number(listing.current_bid || 0);
    const minInc = Number(listing.min_increment || 50);
    const buyNowPrice = listing.buy_now_price ? Number(listing.buy_now_price) : null;
    const minRequired = current ? current + minInc : starting;
    const ended = listing.auction_end && new Date(listing.auction_end).getTime() <= Date.now();
    const isOwner = user && user !== false && user.id === listing.owner_id;
    const isHighest = user && user !== false && user.id === listing.highest_bidder_id;
    const hasReserve = !!listing.has_reserve;
    const reserveMet = listing.reserve_met !== false;
    const reserveNotMet = hasReserve && !reserveMet;
    const reservePrice = listing.reserve_price != null ? Number(listing.reserve_price) : null; // owner only
    const reserveGap = reservePrice != null ? reservePrice - current : null;
    const reserveOffer = listing.reserve_offer || null;
    const offerAccepted = reserveOffer && reserveOffer.status === "accepted";

    useEffect(() => {
        api.get(`/listings/${listing.id}/bids`).then((r) => setBids(r.data)).catch(() => {});
    }, [listing.id, listing.current_bid]);

    // Live updates via WebSocket
    const handleSocketEvent = useCallback((ev) => {
        if (ev.type !== "bid" && ev.type !== "buy_now") return;
        setPulse(true);
        setTimeout(() => setPulse(false), 1200);
        // Refresh listing + bids from the source of truth
        onUpdate?.();
        api.get(`/listings/${listing.id}/bids`).then((r) => setBids(r.data)).catch(() => {});
        // Show subtle live notification for events not triggered by current user
        if (user && user !== false && ev.bidder_name && ev.bidder_name !== (user.company_name || user.name)) {
            if (ev.type === "buy_now") {
                toast.info(`Sofort-Kauf durch ${ev.bidder_name} — Auktion beendet`);
            } else {
                toast.info(`Neues Gebot: ${formatPrice(ev.amount)} von ${ev.bidder_name}`);
            }
        }
    }, [listing.id, onUpdate, user]);

    useAuctionSocket(listing.id, handleSocketEvent);

    useEffect(() => {
        if (!amount) setAmount(String(minRequired));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minRequired]);

    const placeBid = async () => {
        setError("");
        if (!user || user === false) {
            navigate("/login");
            return;
        }
        const value = Number(amount);
        if (!value || value < minRequired) {
            setError(`Mindestgebot: ${formatPrice(minRequired)}`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/listings/${listing.id}/bid`, { amount: value });
            if (data.bought_now) {
                toast.success("Auktion per Sofort-Kauf gewonnen!");
            } else if (data.extended) {
                toast.success("Gebot abgegeben — Auktion um 5 Min verlängert!");
            } else {
                toast.success("Gebot abgegeben!");
            }
            setAmount("");
            onUpdate?.();
            const r = await api.get(`/listings/${listing.id}/bids`);
            setBids(r.data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const buyNow = async () => {
        if (!user || user === false) {
            navigate("/login");
            return;
        }
        if (!buyNowPrice) return;
        setBuyNowOpen(true);
    };

    const confirmBuyNow = async () => {
        setBuyNowOpen(false);
        setBuyingNow(true);
        try {
            await api.post(`/listings/${listing.id}/buy-now`);
            toast.success(`Sofort-Kauf erfolgreich für ${formatPrice(buyNowPrice)}!`);
            onUpdate?.();
            const r = await api.get(`/listings/${listing.id}/bids`);
            setBids(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sofort-Kauf fehlgeschlagen");
        } finally {
            setBuyingNow(false);
        }
    };

    const payNow = async () => {
        if (!user || user === false) { navigate("/login"); return; }
        try {
            const { data } = await api.post(`/auctions/${listing.id}/pay`, {
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Zahlung konnte nicht gestartet werden");
        }
    };

    const [offerAmount, setOfferAmount] = useState("");
    const [offerBusy, setOfferBusy] = useState(false);

    const makeOffer = async () => {
        const val = Number(offerAmount || current);
        if (!val || val <= 0 || val > current) {
            toast.error(`Betrag muss zwischen 1 € und dem Höchstgebot (${formatPrice(current)}) liegen`);
            return;
        }
        setOfferBusy(true);
        try {
            await api.post(`/listings/${listing.id}/reserve-offer`, { amount: val });
            toast.success("Angebot an den Höchstbieter gesendet.");
            onUpdate?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Angebot fehlgeschlagen");
        } finally {
            setOfferBusy(false);
        }
    };

    const respondOffer = async (accept) => {
        setOfferBusy(true);
        try {
            await api.post(`/listings/${listing.id}/reserve-offer/${accept ? "accept" : "decline"}`);
            toast.success(accept ? "Angebot angenommen — bitte bezahlen." : "Angebot abgelehnt.");
            onUpdate?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Antwort fehlgeschlagen");
        } finally {
            setOfferBusy(false);
        }
    };

    return (
        <div className="bg-[#111827] text-white rounded-sm overflow-hidden" data-testid="auction-panel">
            <div className="yellow-stripe h-1.5" />
            <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="bg-[#FFCC00] text-[#111827] px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                        <Gavel className="w-3 h-3" /> Auktion
                    </div>
                    <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${pulse ? "text-red-400" : "text-white/60"}`} data-testid="live-indicator">
                        <span className={`w-1.5 h-1.5 rounded-full bg-red-500 ${pulse ? "animate-ping" : ""}`} />
                        <Radio className="w-3 h-3" /> Live
                    </div>
                    {isHighest && !ended && (
                        <div className="bg-green-600 text-white px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                            <Trophy className="w-3 h-3" /> Sie sind Höchstbietender
                        </div>
                    )}
                    {hasReserve && !ended && (
                        reserveMet ? (
                            <div className="bg-green-600/90 text-white px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="reserve-badge-met">
                                Mindestpreis erreicht
                            </div>
                        ) : (
                            <div className="bg-amber-500 text-[#111827] px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider" data-testid="reserve-badge-not-met">
                                Mindestpreis noch nicht erreicht
                            </div>
                        )
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-5">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1">Aktuelles Gebot</div>
                        <div className="font-display font-black text-3xl text-[#FFCC00] font-mono-num leading-none" data-testid="current-bid">
                            {current ? formatPrice(current) : formatPrice(starting)}
                        </div>
                        <div className="text-xs text-white/60 mt-1">
                            {current ? `${listing.bid_count || 0} Gebote` : `Startpreis`}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2">Endet in</div>
                        <AuctionTimer endIso={listing.auction_end} />
                    </div>
                </div>

                {!ended && !isOwner && (
                    <div className="border-t border-white/10 pt-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2">
                            Ihr Gebot (Mindestgebot: <span className="text-[#FFCC00]">{formatPrice(minRequired)}</span>)
                        </div>
                        <div className="flex gap-2">
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="bg-white text-[#111827] rounded-sm h-12 font-mono-num font-bold text-lg"
                                placeholder={String(minRequired)}
                                data-testid="bid-input"
                            />
                            <Button
                                onClick={placeBid}
                                disabled={submitting}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm h-12 px-6 uppercase tracking-wider"
                                data-testid="bid-submit"
                            >
                                <Gavel className="w-4 h-4 mr-1" /> Bieten
                            </Button>
                        </div>
                        {error && <div className="text-red-400 text-xs mt-2" data-testid="bid-error">{error}</div>}
                        {(!user || user === false) && (
                            <p className="text-xs text-white/60 mt-2">Bitte anmelden, um zu bieten.</p>
                        )}

                        {buyNowPrice && (
                            <div className="mt-4 pt-4 border-t border-white/10">
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 flex items-center gap-1">
                                            <Zap className="w-3 h-3 text-[#FFCC00]" /> Sofort-Kauf
                                        </div>
                                        <div className="font-display font-black text-2xl text-white font-mono-num">
                                            {formatPrice(buyNowPrice)}
                                        </div>
                                    </div>
                                    <Button
                                        onClick={buyNow}
                                        disabled={buyingNow}
                                        variant="outline"
                                        className="border-2 border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-[#111827] bg-transparent font-bold rounded-sm h-11 px-5 uppercase tracking-wider"
                                        data-testid="buy-now-btn"
                                    >
                                        <Zap className="w-4 h-4 mr-1" />
                                        Jetzt kaufen
                                    </Button>
                                </div>
                                <p className="text-[10px] text-white/50">Beendet die Auktion sofort.</p>
                            </div>
                        )}

                        <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2 text-[10px] text-white/50">
                            <Shield className="w-3 h-3 text-[#FFCC00]" />
                            <span>Sniper-Schutz: Gebote in den letzten 5 Min verlängern die Auktion um 5 Min.</span>
                        </div>
                    </div>
                )}

                {isOwner && !ended && (
                    <div className="border-t border-white/10 pt-4 space-y-3">
                        {hasReserve && reservePrice != null && (
                            <div className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid="seller-reserve-status">
                                <div className="flex items-center justify-between text-[11px] text-white/60 mb-1">
                                    <span className="uppercase tracking-wider font-bold">Ihr Mindestpreis (nur für Sie)</span>
                                    <span className="font-mono-num font-bold text-white">{formatPrice(reservePrice)}</span>
                                </div>
                                {reserveGap > 0 ? (
                                    <div className="text-sm text-amber-300" data-testid="seller-reserve-gap">
                                        Noch <span className="font-mono-num font-black">{formatPrice(reserveGap)}</span> bis zum Mindestpreis.
                                        {current > 0 && <span className="text-white/50"> (Höchstgebot {formatPrice(current)})</span>}
                                    </div>
                                ) : (
                                    <div className="text-sm text-green-400 font-bold" data-testid="seller-reserve-met">
                                        Mindestpreis erreicht — das Fahrzeug wird beim Zuschlag verkauft.
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="text-xs text-white/60">Dies ist Ihre Auktion. Sie können nicht selbst bieten.</div>
                    </div>
                )}

                {ended && (
                    <div className="border-t border-white/10 pt-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-red-400 mb-1">Auktion beendet</div>
                        {reserveNotMet ? (
                            <div data-testid="reserve-not-met-ended">
                                <div className="bg-amber-500/15 border border-amber-500/40 text-amber-200 p-3 rounded-sm text-sm">
                                    <div className="font-bold text-amber-300 mb-1">Mindestpreis nicht erreicht — kein Zuschlag</div>
                                    {bids[0] ? (
                                        <span>Höchstgebot <span className="font-mono-num font-bold">{formatPrice(bids[0].amount)}</span> lag unter dem Mindestpreis des Verkäufers. Es kommt kein automatischer Kaufvertrag zustande — weder Käufer noch Verkäufer sind verpflichtet.</span>
                                    ) : (
                                        <span>Es wurden keine Gebote über dem Mindestpreis abgegeben.</span>
                                    )}
                                </div>

                                {/* Verkäufer: Gegenangebot an den Höchstbieter */}
                                {isOwner && bids[0] && !listing.paid && (
                                    <div className="mt-3">
                                        {(!reserveOffer || reserveOffer.status === "declined") && (
                                            <div className="bg-white/5 border border-white/10 rounded-sm p-3" data-testid="seller-make-offer">
                                                {reserveOffer?.status === "declined" && (
                                                    <div className="text-xs text-red-300 mb-2">Ihr Angebot über {formatPrice(reserveOffer.amount)} wurde abgelehnt.</div>
                                                )}
                                                <div className="text-[11px] font-bold uppercase tracking-wider text-white/60 mb-1">Dem Höchstbieter dennoch anbieten</div>
                                                <p className="text-[11px] text-white/50 mb-2">Standard = Höchstgebot ({formatPrice(current)}). Sie können auch weniger anbieten.</p>
                                                <div className="flex gap-2">
                                                    <Input
                                                        type="number"
                                                        value={offerAmount}
                                                        onChange={(e) => setOfferAmount(e.target.value)}
                                                        placeholder={String(Math.round(current))}
                                                        className="bg-white text-[#111827] rounded-sm h-11 font-mono-num font-bold"
                                                        data-testid="offer-amount-input"
                                                    />
                                                    <Button
                                                        onClick={makeOffer}
                                                        disabled={offerBusy}
                                                        className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm h-11 px-4 uppercase tracking-wider"
                                                        data-testid="offer-send-btn"
                                                    >
                                                        Anbieten
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {reserveOffer?.status === "pending" && (
                                            <div className="text-sm text-white/70" data-testid="seller-offer-pending">
                                                Angebot über <span className="font-mono-num font-bold text-[#FFCC00]">{formatPrice(reserveOffer.amount)}</span> gesendet — wartet auf Antwort des Höchstbieters.
                                            </div>
                                        )}
                                        {offerAccepted && (
                                            <div className="mt-2 bg-green-600/20 border border-green-600/40 text-green-300 p-3 rounded-sm text-sm" data-testid="seller-offer-accepted">
                                                Angebot über <span className="font-mono-num font-bold">{formatPrice(reserveOffer.amount)}</span> angenommen — der Käufer bezahlt über die Plattform.
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Höchstbieter: auf das Angebot reagieren */}
                                {isHighest && reserveOffer?.status === "pending" && !listing.paid && (
                                    <div className="mt-3 bg-[#FFCC00]/10 border border-[#FFCC00]/40 p-3 rounded-sm" data-testid="bidder-offer">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00] mb-1">Angebot des Verkäufers</div>
                                        <div className="text-sm mb-3">Der Verkäufer bietet Ihnen das Fahrzeug für <span className="font-mono-num font-black text-[#FFCC00]">{formatPrice(reserveOffer.amount)}</span> an.</div>
                                        <div className="flex gap-2">
                                            <Button onClick={() => respondOffer(true)} disabled={offerBusy}
                                                className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm h-11 uppercase tracking-wider" data-testid="offer-accept-btn">
                                                Annehmen
                                            </Button>
                                            <Button onClick={() => respondOffer(false)} disabled={offerBusy} variant="outline"
                                                className="flex-1 border-2 border-white/30 text-white hover:bg-white/10 bg-transparent font-bold rounded-sm h-11 uppercase tracking-wider" data-testid="offer-decline-btn">
                                                Ablehnen
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Höchstbieter: Angebot angenommen → bezahlen */}
                                {isHighest && offerAccepted && !listing.paid && (
                                    <div className="mt-3 bg-[#FFCC00] text-[#111827] p-4 rounded-sm" data-testid="winner-pay-cta">
                                        <div className="text-[10px] font-bold uppercase tracking-wider mb-1">Angebot angenommen</div>
                                        <div className="font-display font-black text-xl mb-3 font-mono-num">{formatPrice(reserveOffer.amount)}</div>
                                        <Button onClick={payNow} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11 uppercase tracking-wider" data-testid="pay-now-btn">
                                            Jetzt sicher bezahlen
                                        </Button>
                                        <p className="text-[10px] mt-2 text-[#111827]/70">via Stripe · Treuhand-Zahlung an Plattform</p>
                                    </div>
                                )}
                                {isHighest && reserveOffer?.status === "declined" && (
                                    <div className="mt-3 text-sm text-white/60" data-testid="bidder-offer-declined">Sie haben das Angebot abgelehnt.</div>
                                )}
                            </div>
                        ) : bids[0] ? (
                            <div className="text-sm">
                                Zuschlag an <span className="font-bold text-[#FFCC00]">{bids[0].bidder_name}</span> für <span className="font-mono-num font-bold">{formatPrice(bids[0].amount)}</span>
                            </div>
                        ) : (
                            <div className="text-sm text-white/70">Keine Gebote abgegeben</div>
                        )}
                        {isHighest && !reserveNotMet && !listing.paid && (
                            <div className="mt-4 bg-[#FFCC00] text-[#111827] p-4 rounded-sm" data-testid="winner-pay-cta">
                                <div className="text-[10px] font-bold uppercase tracking-wider mb-1">Sie haben gewonnen</div>
                                <div className="font-display font-black text-xl mb-3 font-mono-num">{formatPrice(listing.current_bid)}</div>
                                <Button
                                    onClick={payNow}
                                    className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11 uppercase tracking-wider"
                                    data-testid="pay-now-btn"
                                >
                                    Jetzt sicher bezahlen
                                </Button>
                                <p className="text-[10px] mt-2 text-[#111827]/70">via Stripe · Treuhand-Zahlung an Plattform</p>
                            </div>
                        )}
                        {listing.paid && (
                            <div className="mt-4 bg-green-600 text-white p-3 rounded-sm flex items-center gap-2" data-testid="paid-badge">
                                <Trophy className="w-4 h-4" />
                                <span className="text-sm font-bold">Bezahlt — Übergabe wird mit Verkäufer vereinbart</span>
                            </div>
                        )}
                    </div>
                )}

                {bids.length > 0 && (
                    <div className="border-t border-white/10 pt-4 mt-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2 flex items-center gap-1">
                            <History className="w-3 h-3" /> Bietverlauf ({bids.length})
                        </div>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="bid-history">
                            {bids.map((b, idx) => (
                                <div key={b.id} className={`flex items-center justify-between text-sm py-1.5 px-2 rounded-sm ${idx === 0 ? "bg-[#FFCC00]/20 text-[#FFCC00]" : "text-white/80"}`}>
                                    <span className="truncate max-w-[60%]">{b.bidder_name}</span>
                                    <span className="font-mono-num font-bold">{formatPrice(b.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <AlertDialog open={buyNowOpen} onOpenChange={setBuyNowOpen}>
                <AlertDialogContent className="rounded-sm" data-testid="buy-now-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display font-black text-2xl flex items-center gap-2">
                            <Zap className="w-6 h-6 text-[#FFCC00]" /> Sofort kaufen?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3 pt-2">
                            <span className="block">
                                Sie kaufen <strong className="text-[#111827]">{listing.title}</strong> für{" "}
                                <strong className="text-[#111827] font-mono-num text-lg">{formatPrice(buyNowPrice)}</strong>.
                            </span>
                            <span className="block bg-[#FFCC00]/20 border-l-4 border-[#FFCC00] px-3 py-2 text-xs text-[#111827]">
                                Die Auktion wird <strong>sofort beendet</strong>, andere Bieter haben dann keine Chance mehr.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm" data-testid="buy-now-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmBuyNow}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider"
                            data-testid="buy-now-confirm-btn"
                        >
                            <Zap className="w-4 h-4 mr-1" /> Jetzt kaufen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
